import Veraportal from "./Veraportal";
import Tooltip from './Tooltip';

/**
 * Schnittstelle, die die Struktur eines einzelnen Menüeintrags definiert.
 */
interface Imenuitem {
    bezeichnung: string, // Der Anzeigetext des Menüpunkts.
    icon: string, // Der Name des Bootstrap-Icons.
    href?: string | null, // Die URL, zu der der Menüpunkt verlinkt.
    id?: string | null, // Wenn als Button gerendert wird, ist eine ID erforderlich
    target: string | null, // Das Ziel des Links (z.B. `_blank`).
    startTime: string | null, // Startzeitpunkt für die Sichtbarkeit des Menüpunkts.
    endTime: string | null, // Endzeitpunkt für die Sichtbarkeit des Menüpunkts.
    laender?: string[], // Nur in diesen Bundesländern anzeigen. Wenn empty oder unset, dann in allen Ländern anzeigen
    enabled?: boolean, // Gibt an, ob der Menüpunkt aktiviert ist
    type?: 'left' | 'right', // Ausrichtung des Menüpunkts im Hauptmenü.
    submenu?: Imenuitem[] // Eine Liste von Untermenüpunkten.
}

/**
 * Verwaltet das Hauptnavigationsmenü der Anwendung.
 * Diese Klasse wird als Singleton implementiert, um sicherzustellen, dass nur eine Instanz des Hauptmenüs existiert.
 */
export default class Hauptmenu {
    private static _instance: Hauptmenu; // Die statische Singleton-Instanz.
    private _veraportal: Veraportal; // Referenz auf die Hauptanwendungsklasse Veraportal.
    private _hauptmenuDOM: HTMLDivElement // Das DOM-Element des Hauptmenüs.

    /**
     * Gibt die Singleton-Instanz der Hauptmenu-Klasse zurück.
     * Erstellt eine neue Instanz, falls noch keine existiert.
     * @param {Veraportal} veraportal - Die Instanz der Hauptanwendung.
     * @returns {Hauptmenu} Die Singleton-Instanz.
     */
    public static getInstance(veraportal: Veraportal): Hauptmenu {
        return this._instance || (this._instance = new this(veraportal));
    }

    /**
     * Privater Konstruktor, um das Singleton-Muster zu erzwingen.
     * Initialisiert die Eigenschaften und erstellt das DOM-Element für das Menü, falls es nicht vorhanden ist.
     * @param {Veraportal} veraportal - Die Instanz der Hauptanwendung.
     */
    private constructor(veraportal: Veraportal) {
        this._veraportal = veraportal;
        this._hauptmenuDOM = document.querySelector(`#hauptmenu`) ?? null;

        // Wenn das DOM-Element für das Hauptmenü nicht gefunden wird, wird es neu erstellt.
        if (this._hauptmenuDOM === null) {
            this._hauptmenuDOM = document.createElement('nav') as HTMLDivElement;
            this._hauptmenuDOM.id = 'hauptmenu';
            this._hauptmenuDOM.innerHTML = `
            <div>
                <div class='logo'>
                    <a class='item' href='${(this._veraportal.sessionvariablen.user.accounttyp === 99) ? `/wasserstand.php` : `/startseite.php`}' aria-label="Zur Startseite" data-tooltip="${Tooltip.html`
                        <p><strong>Zur Startseite</strong></p>
                    `}">
                        <img src='/img/logos/logo-${this._veraportal.sessionvariablen.projekt.weiche}${(this._veraportal.sessionvariablen.schule.land === 'ni') ? '-ni' : ''}.svg' alt='Projektlogo'/>
                    </a>
                </div>
                <div class="navigations">
                    <div class="left"></div>
                    <div class="right"></div>
                </div>
                <button id="hamburger-button">
                    <svg role='graphics-symbol'>
                        <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#list'/>
                    </svg>
                </button>
            </div>
            `;
        }
        // Fügt das Hauptmenü am Anfang des <body>-Elements ein.
        document.body?.prepend(this._hauptmenuDOM);
    }

    /**
     * Erstellt den HTML-Inhalt des Hauptmenüs neu und initialisiert die Event-Listener.
     */
    public refresh(menu: Imenuitem[]) {
        // Die Datenstruktur für die linksbündigen Menüeinträge.

        // Generiert die HTML-Struktur für die linksbündigen Menüeinträge.
        this._hauptmenuDOM.querySelector(`div.navigations > div.left`).innerHTML = menu
            .map(m => {

                if (m.type !== 'left' && m.type !== null)
                    return ``; // Überspringt Menüpunkte, die nicht explizit für die linke Seite vorgesehen sind.

                if (m.laender !== undefined && m.laender?.includes(this._veraportal.sessionvariablen.schule.land) === false)
                    return ``; // Überspringt Menüpunkte, in diesem Land nicht angezeigt werden sollen

                let disabled: boolean = false;

                // Prüfe, ob der Menüpunkt explizit deaktiviert ist
                if ((m.enabled ?? true) !== true)
                    disabled = true;

                // Prüfe, ob der Menüpunkt aufgrund von Zeitbeschränkungen deaktiviert werden soll
                if (
                    disabled === true || // Ist bereits deaktiviert
                    (
                        (m.startTime !== null && new Date(m.startTime) > this._veraportal.portalzeit) || // Startzeit liegt in der Zukunft
                        (m.endTime !== null && new Date(m.endTime) < this._veraportal.portalzeit) // Endzeit liegt in der Vergangenheit
                    )
                )
                    disabled = true;

                // Erstellt einen einfachen Link, wenn kein Untermenü vorhanden ist.
                if (m.submenu.length === 0) {
                    return `
                    <a ${((m.id ?? null) !== null) ? `id="${m.id}"` : ``} class="item ${(disabled) ? `disabled` : ``}"
                    role="menuitem" ${((m.href ?? null) !== null) ? `href="${m.href}"` : ``}" ${((m.href ?? null) !== null && m.target !== null) ? `target="${m.target}"` : ``}>
                        <div class="caption">
                            <svg role='graphics-symbol'>
                                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#${m.icon}'/>
                            </svg>
                            ${m.bezeichnung}
                        </div>
                    </a>
                    `;
                } else { // Erstellt einen Menüpunkt mit einem ausklappbaren Untermenü.
                    return `
                    <div class="item ${(disabled) ? `disabled` : ``}" role="menuitem">
                        <div class="caption">
                            <svg role='graphics-symbol'>
                                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#${m.icon}'/>
                            </svg>
                            ${m.bezeichnung}
                            <svg class="caret">
                                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#caret-down-fill'/>
                            </svg>
                        </div>
                        <div class="submenu">
                        ${m.submenu.map(m2 => {
                        if (m2.laender !== undefined && m2.laender?.includes(this._veraportal.sessionvariablen.schule.land) === false)
                            return ``; // Überspringt Menüpunkte, die in diesem Land nicht angezeigt werden sollen


                        let disabled: boolean = false;
                        // Prüfe, ob der Untermenüpunkt explizit deaktiviert ist
                        if ((m2.enabled ?? true) !== true)
                            disabled = true;
                        // Prüfe, ob der Untermenüpunkt aufgrund von Zeitbeschränkungen deaktiviert werden soll
                        if (
                            disabled === true || // Ist bereits deaktiviert
                            (
                                (m2.startTime !== null && new Date(m2.startTime) > this._veraportal.portalzeit) || // Startzeit liegt in der Zukunft
                                (m2.endTime !== null && new Date(m2.endTime) < this._veraportal.portalzeit) // Endzeit liegt in der Vergangenheit
                            )
                        )
                            disabled = true;

                        return `
                            <a class="item ${(disabled) ? `disabled` : ``}" role="menuitem" href="${(m2.href !== null) ? `${m2.href}` : `#`}" ${(m2.target !== null) ? `target="${m2.target}"` : ``}>
                                <div class="caption">
                                    <svg role='graphics-symbol'>
                                        <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#${m2.icon}'/>
                                    </svg>
                                    ${m2.bezeichnung}
                                </div>
                            </a>
                        `
                    }).join('\n')}
                        </div>
                    </div>
                    `;
                }
            }).join('\n');

        // TODO:low rechtsbündiges Menü auch dynamisieren
        // Füllt das rechtsbündige Menü mit statischem HTML.
        this._hauptmenuDOM.querySelector(`div.navigations > div.right`).innerHTML = `
        ${([5, 8].includes(this._veraportal.sessionvariablen.user.accounttyp)) ? `
        <div class="lerngruppenanzeige"
            ${(this._veraportal.sessionvariablen.user.accounttyp === 5) ? `data-tooltip="${Tooltip.html`
                <p><strong>Lerngruppenzugang</strong></p>
                <p>Dieser Zugang verwaltet eine einzelne Testgruppe. Wenden Sie sich für jahrgangsübergreifende Anpassungen an Ihre ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Koordination.</p>
            `}"` : ``}
            ${(this._veraportal.sessionvariablen.user.accounttyp === 8) ? `data-tooltip="${Tooltip.html`
                <p><strong>Gesonderter Lerngruppenzugang</strong></p>
                <p>Da die Schülerinnen und Schüler nicht zum regulären ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Jahrgang gehören, werden sie über diesen Zugang verwaltet.</p>
            `}"` : ``}
            >
            <div class="label">Lerngruppenzugang:</div>
            <div class="lerngruppe-bezeichnung">
                ${this._veraportal.sessionvariablen.user.username}
            </div>
            ${(this._veraportal.sessionvariablen.user.accounttyp === 8) ? `<div class="accounttyp">Franz. 9. Klasse</div>` : ``}
        </div>
        ` : ``}
        ${([8, 99].includes(this._veraportal.sessionvariablen.user.accounttyp) === false) ? `
        <div id="tresor-schalter" data-tresor-offen="0">
            <button class="geschlossen">
                <svg role='graphics-symbol'>
                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#key'/>
                </svg>
                Klarnamen<br/>entschlüsseln
            </button>
            <button class="offen" disabled="disabled">
                <svg role='graphics-symbol'>
                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#check-lg'/>
                </svg>
                Klarnamen<br/>aktiv
            </button>
        </div>
        <a id='menu-benachrichtigungscenter-btn' class='item' href='/benachrichtigungscenter.php' aria-label='Benachrichtigungscenter' data-tooltip="${Tooltip.html`
            <p><strong>Benachrichtigungscenter</strong></p>
        `}">
            <div class="caption">
                <svg role='graphics-symbol'>
                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#bell-fill'/>
                </svg>
                <span class="benachrichtigungen"></span>
            </div>
        </a>
        ` : ``}
        
        ${(this._veraportal.sessionvariablen.user.accounttyp === 99) ? `
        <a id='menu-wasserstand-dokumentation-btn' class='item' href='/wasserstand-readme.html' target="wasserstand-readme"
        aria-label='Dokumentation des Wasserstands öffnen' data-tooltip="${Tooltip.html`
            <p><strong>Dokumentation</strong></p>
            <p>Öffnet die Dokumentation des Wasserstands und Druckbedarfexports für Ländervertretungen.</p>
        `}">
            <div class="caption">
                <svg role='graphics-symbol'>
                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#question-circle'/>
                </svg>
            </div>
        </a>
        ` : ``}
        
        <a id='menu-logout-btn' class='item' href='/logout.php' aria-label='Abmelden' data-tooltip="${Tooltip.html`
            <p><strong>Abmelden</strong></p>
        `}">
            <div class="caption">
                <svg role='graphics-symbol'>
                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#box-arrow-right'/>
                </svg>
            </div>
        </a>
        `;

        this.breadcrumbAktualisieren();

        // Initialisiert die Funktionalität der Untermenüs (Auf-/Zuklappen).
        this.initSubmenus();

        this._hauptmenuDOM.querySelector(`#hamburger-button`)
            ?.addEventListener('click', async () => {
                this._hauptmenuDOM.classList.toggle('hamburger-active')
            });

        // Fügt dem "Tresor-Schalter" einen Event-Listener hinzu, um den Tresor zu aktivieren.
        this._hauptmenuDOM.querySelector(`#tresor-schalter button`)
            ?.addEventListener('click', async () => {
                await this._veraportal.tresor.aktivieren();
            });

        // Entfernt alle Links aus der Tab-Reihenfolge, um die Barrierefreiheit zu steuern.
        this._hauptmenuDOM.querySelectorAll(`a`)
            ?.forEach(link => link.setAttribute('tabindex', '-1'));

    }

    /** Ergänzt den aktuellen Menüpfad am Anfang des Seiteninhalts */
    private breadcrumbAktualisieren(): void {
        if (document.body.id === 'cbt-zugangscodeliste-page')
            return;

        const main = document.querySelector<HTMLElement>('body > main');
        const seitentitel = document.body.dataset.seitentitel?.trim() ?? '';
        if (main === null || seitentitel === '')
            return;

        main.querySelector(':scope > nav.breadcrumb')?.remove();
        main.classList.remove('ohne-breadcrumb');

        const normalisierePfad = (pfad: string): string => pfad.replace(/\/+$/, '') || '/';
        const aktuellerPfad = normalisierePfad(window.location.pathname);
        if (aktuellerPfad === '/startseite.php') {
            main.classList.add('ohne-breadcrumb');
            return;
        }

        const aktuellerMenulink = Array.from(this._hauptmenuDOM.querySelectorAll<HTMLAnchorElement>('a[href]'))
            .find((link): boolean => normalisierePfad(new URL(link.href, window.location.origin).pathname) === aktuellerPfad) ?? null;
        const elternMenu = aktuellerMenulink?.closest('div.submenu')?.closest('div.item') ?? null;
        const elternBezeichnung = elternMenu?.querySelector<HTMLElement>(':scope > div.caption')?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
        if (elternBezeichnung === '') {
            main.classList.add('ohne-breadcrumb');
            return;
        }

        const breadcrumb = document.createElement('nav');
        breadcrumb.className = 'breadcrumb';
        breadcrumb.setAttribute('aria-label', 'Breadcrumb');

        const liste = document.createElement('ol');
        breadcrumb.append(liste);

        const elternElement = document.createElement('li');
        elternElement.append(elternBezeichnung);
        liste.append(elternElement);

        const aktuelleSeite = document.createElement('li');
        const ueberschrift = document.createElement('h1');
        const link = document.createElement('a');
        link.href = window.location.href;
        link.textContent = seitentitel;
        link.setAttribute('aria-current', 'page');
        ueberschrift.append(link);
        aktuelleSeite.append(ueberschrift);
        liste.append(aktuelleSeite);

        main.prepend(breadcrumb);
    }

    /**
     * Initialisiert die Event-Listener für das Öffnen und Schließen der Untermenüs.
     */
    private initSubmenus() {
        // Fügt jedem Menüpunkt mit Untermenü einen Klick-Listener hinzu.
        this._hauptmenuDOM.querySelectorAll(`div.navigations div.item`).forEach(item => {
            item.addEventListener('click', (event) => {
                event.stopPropagation(); // Verhindert, dass der Klick das Dokument erreicht und alle Menüs schließt.
                const submenu = item.querySelector(`div.submenu`);
                // Schließt alle anderen geöffneten Untermenüs.
                this._hauptmenuDOM.querySelectorAll(`div.submenu.active`)
                    .forEach(activeSubmenu => {
                        if (activeSubmenu !== submenu) {
                            activeSubmenu.classList.remove('active');
                        }
                    });
                // Schaltet die 'active'-Klasse für das geklickte Untermenü um.
                submenu?.classList.toggle('active');
            });
        });

        // Fügt einen globalen Klick-Listener hinzu, um alle offenen Untermenüs zu schließen,
        // wenn außerhalb des Menüs geklickt wird.
        document.addEventListener('click', () => {
            this._hauptmenuDOM.querySelectorAll(`div.submenu.active`)
                .forEach(submenu => {
                    submenu.classList.remove('active');
                });
        });
    }
}
